import * as React from "react";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "../../../components/mui";
import Modal from "../../../components/shared/Modal";
import { useToast } from "../../../components/shared/ToastProvider";

import "../../../styles/main.css";
import "../../../styles/pages/condoUnitDetail.css";

// UI-only form for creating/updating a misc expense payment.
export default function MiscPaymentFormModal({
  open,
  onClose,
  mode = "add", // "add" | "edit"
  condoItems = [], // kept for future backend wiring, currently unused
  initialValues = {}
}) {
  const isEdit = mode === "edit";
  const { showToast } = useToast();

  const handlePrimaryClick = () => {
    showToast({
      message: isEdit ? "Misc expense payment updated" : "Misc expense payment created",
      severity: "success"
    });
    if (onClose) onClose();
  };

  const actions = (
    <>
      <Button variant="outlined" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="contained" onClick={handlePrimaryClick}>
        {isEdit ? "Update" : "Create"}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit Misc Expense Payment" : "Add Misc Expense Payment"}
      actions={actions}
      maxWidth="md"
      fullWidth
    >
      {/* Form fields - based on reference */}
      <Box className="modal-form-grid modal-form-grid--spaced">
        {/* Condo Company (single line) */}
        <Box className="modal-form-row">
          <FormControl fullWidth required size="small">
            <InputLabel>Condo Company</InputLabel>
            <Select
              defaultValue={initialValues.condoCompany || "Caspo"}
              label="Condo Company"
            >
              <MenuItem value="Caspo">Caspo</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Condo Unit (single line) */}
        <Box className="modal-form-row">
          <FormControl fullWidth required size="small">
            <InputLabel>Condo Unit</InputLabel>
            <Select
              defaultValue={initialValues.condoUnit || "Three Central - 28F Three Central"}
              label="Condo Unit"
            >
              <MenuItem value="Three Central - 28F Three Central">Three Central - 28F Three Central</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Expense Type / Official Receipt No / Billing No (one line) */}
        <Box className="modal-form-row" display="flex" gap={1}>
          <FormControl fullWidth required size="small">
            <InputLabel>Expense Type</InputLabel>
            <Select
              defaultValue={initialValues.expenseType || "Internet"}
              label="Expense Type"
            >
              <MenuItem value="Internet">Internet</MenuItem>
              <MenuItem value="Appliance">Appliance</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            required
            size="small"
            label="Official Receipt No"
            defaultValue={initialValues.officialReceiptNo || ""}
          />
          <TextField
            fullWidth
            size="small"
            label="Billing No"
            defaultValue={initialValues.billingNo || ""}
          />
        </Box>

        {/* RF No / Check No / Amount */}
        <Box className="modal-form-row" display="flex" gap={1}>
          <FormControl fullWidth size="small">
            <InputLabel>RF No</InputLabel>
            <Select
              defaultValue={initialValues.rfNo || "-"}
              label="RF No"
            >
              <MenuItem value="-">-</MenuItem>
              <MenuItem value="M-2025-001">M-2025-001</MenuItem>
              <MenuItem value="M-2025-002">M-2025-002</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            label="Check No"
            defaultValue={initialValues.checkNo || ""}
          />
          <TextField
            fullWidth
            required
            size="small"
            type="number"
            label="Amount"
            placeholder="0.00"
            defaultValue={initialValues.amountValue || ""}
            inputProps={{ step: "0.01" }}
          />
        </Box>

        {/* Dates: Acct Entry, Payment Date, Payment From, Payment To */}
        <Box className="modal-form-row" display="flex" gap={1}>
          <TextField
            fullWidth
            required
            size="small"
            type="date"
            label="Acct Entry Date"
            InputLabelProps={{ shrink: true }}
            defaultValue={initialValues.acctEntryDateRaw || ""}
          />
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Payment Date"
            InputLabelProps={{ shrink: true }}
            defaultValue={initialValues.paymentDateRaw || ""}
          />
          <TextField
            fullWidth
            required
            size="small"
            type="date"
            label="Payment From"
            InputLabelProps={{ shrink: true }}
            defaultValue={initialValues.paymentFromRaw || ""}
          />
          <TextField
            fullWidth
            required
            size="small"
            type="date"
            label="Payment To"
            InputLabelProps={{ shrink: true }}
            defaultValue={initialValues.paymentToRaw || ""}
          />
        </Box>

        {/* Payable To / Condo Sharing (one line) */}
        <Box className="modal-form-row" display="flex" gap={1}>
          <TextField
            fullWidth
            size="small"
            label="Payable To"
            defaultValue={initialValues.payableTo || ""}
          />
          <FormControl fullWidth required size="small">
            <InputLabel>Condo Sharing</InputLabel>
            <Select
              defaultValue={initialValues.condoSharing || "Not Shared"}
              label="Condo Sharing"
            >
              <MenuItem value="Not Shared">Not Shared</MenuItem>
              <MenuItem value="Shared">Shared</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Remarks */}
        <TextField
          fullWidth
          multiline
          rows={3}
          size="small"
          className="modal-form-row"
          label="Remarks"
          defaultValue={initialValues.remarks || ""}
        />
      </Box>
    </Modal>
  );
}
